import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlQuestionService, ControlQuestion, ControlQuestionOption, ControlQuestionSurvey } from '../../services/controlquestion.service';
import { API_BASE_URL } from '../../services/api-base';

@Component({
  selector: 'app-controlquestion-public',
  templateUrl: './controlquestion-public.component.html',
  styleUrls: ['./controlquestion-public.component.scss']
})
export class ControlquestionPublicComponent implements OnInit {
  private readonly apiBase = `${API_BASE_URL}`;
  token = '';
  password = '';
  loading = false;
  errorMessage = '';
  survey: ControlQuestionSurvey | null = null;
  tenantBrand: { id: number; tenant_name?: string | null; logo_url?: string | null } | null = null;
  questions: ControlQuestion[] = [];
  options: ControlQuestionOption[] = [];
  answers: Record<number, any> = {};
  submitted = false;
  redirectingAfterSubmit = false;
  responseId: number | null = null;
  respondentUserId: number | null = null;
  recipientId: number | null = null;
  isInternalContext = false;
  isReadOnly = false;
  private autoAccessAttempted = false;

  constructor(
    private route: ActivatedRoute,
    private controlquestion: ControlQuestionService,
    private router: Router
  ) {}

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    const navState = history.state || {};
    if (!this.token && navState?.token) {
      this.token = String(navState.token);
    }
    if (navState?.respondent_user_id) {
      this.respondentUserId = Number(navState.respondent_user_id) || null;
    }
    if (navState?.recipient_id) {
      this.recipientId = Number(navState.recipient_id) || null;
    }
    if (navState?.read_only === 1 || navState?.read_only === '1' || navState?.read_only === true) {
      this.isReadOnly = true;
    }
    this.isInternalContext = !!(this.respondentUserId && this.recipientId);

    this.route.queryParamMap.subscribe((params) => {
      const password = params.get('password');
      const autostart = params.get('autostart');
      const userId = params.get('user_id');
      const recipientId = params.get('recipient_id');
      const readOnly = params.get('read_only');
      if (userId) {
        this.respondentUserId = Number(userId) || null;
      }
      if (recipientId) {
        this.recipientId = Number(recipientId) || null;
      }
      if (readOnly === '1') {
        this.isReadOnly = true;
      }
      this.isInternalContext = !!(this.respondentUserId && this.recipientId);
      if (password != null) {
        this.password = password;
      }
      const stateAutostart = navState?.autostart === 1 || navState?.autostart === '1';
      const shouldAutoStart =
        (password != null && password !== '') ||
        autostart === '1' ||
        stateAutostart ||
        !this.autoAccessAttempted;
      if (shouldAutoStart) {
        this.autoAccessAttempted = true;
        this.accessSurvey();
      }
    });
  }

  getOptions(questionId: number) {
    return this.options.filter((opt) => opt.question_id === questionId);
  }

  getRatingOptions(questionId: number) {
    return this.getOptions(questionId)
      .map((option) => ({ option, score: this.getOptionScore(option.label) }))
      .sort((a, b) => a.score - b.score)
      .map((entry) => entry.option);
  }

  getSelectedRatingScore(questionId: number) {
    const selectedValue = Number(this.answers[questionId] || 0);
    if (!selectedValue) {
      return 0;
    }
    const selected = this.getOptions(questionId).find((option) => option.id === selectedValue);
    if (selected) {
      return this.getOptionScore(selected.label);
    }
    if (selectedValue >= 1 && selectedValue <= 5) {
      return selectedValue;
    }
    return 0;
  }

  selectRatingScore(questionId: number, score: number) {
    if (this.isReadOnly) {
      return;
    }
    const option = this.getOptionByScore(questionId, score);
    if (option) {
      this.answers[questionId] = option.id;
      return;
    }
    const normalized = Math.max(1, Math.min(5, Number(score) || 1));
    this.answers[questionId] = normalized;
  }

  getEmojiSymbol(label?: string | null) {
    const text = String(label || '').trim();
    if (!text) {
      return '🙂';
    }
    const firstToken = text.split(/\s+/)[0];
    const looksLikeEmoji = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(firstToken);
    return looksLikeEmoji ? firstToken : '🙂';
  }

  getEmojiText(label?: string | null) {
    const text = String(label || '').trim();
    if (!text) {
      return '';
    }
    const firstToken = text.split(/\s+/)[0];
    const looksLikeEmoji = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(firstToken);
    return looksLikeEmoji ? text.replace(firstToken, '').trim() : text;
  }

  getOptionDisplayText(option: ControlQuestionOption) {
    const label = String(option?.label || '').trim();
    if (!label) {
      return '';
    }
    if (this.isLikelyFileName(label)) {
      return '';
    }
    return label;
  }

  getStarCount(label?: string | null) {
    const text = String(label || '');
    const match = text.match(/(\d+)/);
    const parsed = match ? Number.parseInt(match[1], 10) : 1;
    if (!Number.isFinite(parsed)) {
      return 1;
    }
    return Math.max(1, Math.min(5, parsed));
  }

  getStarArray(label?: string | null) {
    return Array.from({ length: this.getStarCount(label) });
  }

  get brandLogoUrl() {
    const logo = String(this.tenantBrand?.logo_url || '').trim();
    if (!logo) {
      return 'assets/images/controlps-logo.svg';
    }
    if (logo.startsWith('http://') || logo.startsWith('https://')) {
      return logo;
    }
    return `${this.apiBase}${logo}`;
  }

  get answeredCount() {
    return this.questions.filter((question) => this.isAnswered(question)).length;
  }

  get progressPercent() {
    const total = this.questions.length;
    if (!total) {
      return 0;
    }
    return Math.round((this.answeredCount / total) * 100);
  }

  accessSurvey() {
    if (!this.token) {
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.controlquestion
      .validatePublicLink(this.token, {
        password: this.password || null,
        respondent_user_id: this.respondentUserId || null,
        recipient_id: this.recipientId || null,
        read_only: this.isReadOnly
      })
      .subscribe({
      next: (response: any) => {
        this.loading = false;
        this.tenantBrand = response.tenant || null;
        this.survey = response.survey;
        this.questions = response.questions || [];
        this.options = response.options || [];
        this.responseId = response.response_id || null;
        this.isReadOnly = !!response?.read_only;
        this.applyPrefilledAnswers(response?.response_answers || []);
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.error?.error || 'Não foi possível acessar o questionário.';
      }
    });
  }

  toggleMulti(questionId: number, optionId: number) {
    if (this.isReadOnly) {
      return;
    }
    const current = Array.isArray(this.answers[questionId]) ? [...this.answers[questionId]] : [];
    const index = current.indexOf(optionId);
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(optionId);
    }
    this.answers[questionId] = current;
  }

  private isAnswered(question: ControlQuestion) {
    const value = this.answers[question.id];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value);
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (value && typeof value === 'object') {
      return Object.keys(value).length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  }

  private getOptionScore(label?: string | null) {
    const text = String(label || '');
    const match = text.match(/(\d+)/);
    const parsed = match ? Number.parseInt(match[1], 10) : 0;
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(1, Math.min(5, parsed));
  }

  private getOptionByScore(questionId: number, score: number) {
    const normalized = Math.max(1, Math.min(5, score));
    const options = this.getRatingOptions(questionId);
    const exact = options.find((option) => this.getOptionScore(option.label) === normalized);
    if (exact) {
      return exact;
    }
    return options[normalized - 1] || options[0] || null;
  }

  private isLikelyFileName(value: string) {
    const text = String(value || '').trim().toLowerCase();
    if (!text) {
      return false;
    }
    return /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif|mp4|webm|mov|avi|mkv|mpeg|mpg|m4v)$/i.test(text);
  }

  submit() {
    if (this.isReadOnly || !this.token || !this.questions.length || !this.responseId) {
      return;
    }
    const payloadAnswers = this.questions.map((question) => {
      const value = this.answers[question.id];
      if (Array.isArray(value)) {
        return { question_id: question.id, answer_json: value };
      }
      if (typeof value === 'object' && value !== null) {
        return { question_id: question.id, answer_json: value };
      }
      return { question_id: question.id, answer_text: value ?? '' };
    });
    this.loading = true;
    this.controlquestion
      .submitResponse(this.token, {
        response_id: this.responseId,
        respondent_user_id: this.respondentUserId,
        recipient_id: this.recipientId,
        answers: payloadAnswers
      })
      .subscribe({
      next: () => {
        this.loading = false;
        this.submitted = true;
        if (this.isInternalContext) {
          this.redirectingAfterSubmit = true;
          setTimeout(() => {
            this.router.navigate(['/controlquestion-intranet']);
          }, 1200);
        }
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Não foi possível enviar as respostas.';
      }
    });
  }

  private applyPrefilledAnswers(items: Array<{ question_id: number; answer_text?: any; answer_json?: any }>) {
    if (!Array.isArray(items) || !items.length) {
      return;
    }
    const mapped: Record<number, any> = {};
    for (const item of items) {
      const questionId = Number(item?.question_id || 0);
      if (!questionId) {
        continue;
      }
      const question = this.questions.find((q) => q.id === questionId);
      if (!question) {
        continue;
      }
      let parsedJson: any = null;
      if (item?.answer_json !== undefined && item?.answer_json !== null && item?.answer_json !== '') {
        if (typeof item.answer_json === 'string') {
          try {
            parsedJson = JSON.parse(item.answer_json);
          } catch {
            parsedJson = null;
          }
        } else {
          parsedJson = item.answer_json;
        }
      }
      const rawText = item?.answer_text;
      if (question.question_type === 'MULTIPLE_CHOICE') {
        if (Array.isArray(parsedJson)) {
          mapped[questionId] = parsedJson.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value));
          continue;
        }
        if (typeof rawText === 'string' && rawText.trim()) {
          mapped[questionId] = rawText
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => Number.isFinite(value));
          continue;
        }
        mapped[questionId] = [];
        continue;
      }
      if (['SINGLE_CHOICE', 'DROPDOWN', 'RANKING', 'IMAGE_CHOICE', 'EMOJI', 'STAR', 'SCALE'].includes(question.question_type)) {
        const numeric = Number(rawText);
        if (Number.isFinite(numeric) && numeric > 0) {
          mapped[questionId] = numeric;
        } else if (parsedJson !== null) {
          mapped[questionId] = parsedJson;
        } else {
          mapped[questionId] = rawText ?? '';
        }
        continue;
      }
      if (parsedJson !== null) {
        mapped[questionId] = parsedJson;
      } else {
        mapped[questionId] = rawText ?? '';
      }
    }
    this.answers = mapped;
  }
}
